import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SectionCard } from '@/components/ui/section-card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { JackpotCard } from '@/components/jackpots/JackpotCard';
import { useJackpotsList } from '@/api/extensions/useJackpotOps';

interface BrowseUpcomingSectionProps {
  /** Discriminator on the rodeos table — drives both the API filter and the
   *  detail-page route ('/jackpots/:id' vs '/rodeos/:id'). Defaults to
   *  jackpot to preserve the original single-column dashboard behavior. */
  type?: 'jackpot' | 'rodeo';
}

export function BrowseUpcomingSection({
  type = 'jackpot',
}: BrowseUpcomingSectionProps = {}) {
  const navigate = useNavigate();
  const { data, isLoading } = useJackpotsList({
    is_published: true,
    pageSize: 200,
    type,
  });

  const isRodeo = type === 'rodeo';
  const sectionTitle = isRodeo ? 'Upcoming Rodeos' : 'Upcoming Jackpots';
  const viewAllPath = isRodeo ? '/rodeos' : '/jackpots';
  const detailPath = (id: string) =>
    isRodeo ? `/rodeos/${id}` : `/jackpots/${id}`;

  const upcoming = useMemo(() => {
    const all = data?.data ?? [];
    const todayStr = new Date().toISOString().split('T')[0];
    return all
      .filter((j: any) => {
        const start = j.start_date?.substring(0, 10) ?? '';
        const end = j.end_date?.substring(0, 10) ?? start;
        return end >= todayStr; // upcoming or current
      })
      .sort((a: any, b: any) => {
        const dateA = a.start_date || '';
        const dateB = b.start_date || '';
        return dateA.localeCompare(dateB);
      })
      .slice(0, 4); // Show max 4 per column
  }, [data]);

  if (isLoading) {
    return (
      <SectionCard title={sectionTitle}>
        <div className="grid grid-cols-1 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-36 rounded-xl" />
          ))}
        </div>
      </SectionCard>
    );
  }

  if (upcoming.length === 0) {
    return (
      <SectionCard title={sectionTitle}>
        <p className="text-sm text-muted-foreground">
          No upcoming {isRodeo ? 'rodeos' : 'jackpots'}.
        </p>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title={sectionTitle}
      headerRight={
        <Button
          variant="ghost"
          size="sm"
          className="text-xs text-inherit opacity-80 hover:opacity-100 h-auto py-0 px-2"
          onClick={() => navigate(viewAllPath)}
        >
          View All <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      }
    >
      <div className="grid grid-cols-1 gap-3">
        {upcoming.map((j: any) => (
          <JackpotCard
            key={j.id}
            jackpot={j}
            onClick={() => navigate(detailPath(j.id))}
          />
        ))}
      </div>
    </SectionCard>
  );
}
